/**
 * Helpers for mapping Monarch PDM events to PDP callbacks and side effects.
 */
import analytics from 'services/analytics';
import { userAddedProductFromPDP } from 'services/analytics/analytics';
import { UserFavoriteActions } from 'services/Data';
import { bugsnagError } from 'services/bugsnag';
import ProductSearch from 'services/ProductSearch';

import {
  getCurrentActiveDesign,
  getCurrentSelectedHotspotProduct,
  getCurrentPackageData
} from 'redux/Studio';
import { getCurrentSearchKey } from 'redux/StudioProductBrowser';
import { getCurrentUserEmail, getCurrentUserId } from 'redux/User';
import { addProductToCart } from 'shared/components/Checkout/state';

export function handleLikeDislikeChange ({
  event,
  fromShop,
  onLikeDislikeProduct,
  product,
  reduxDispatcher,
  spaceId
}) {
  const { newLikeStatus, productId } = event.payload;

  // Fire the API request.
  reduxDispatcher(UserFavoriteActions.updateLikeStatuses({
    productId: Number(productId),
    likeStatus: newLikeStatus,
    source: spaceId || undefined
  }));

  // Fire tracking.
  if (typeof onLikeDislikeProduct === 'function') {
    const likeStatus = (newLikeStatus > 0)
      ? 'LIKE'
      : (newLikeStatus < 0 ? 'DISLIKE' : 'NEUTRAL');

    onLikeDislikeProduct({
      product,
      likeStatus,
      origin: fromShop ? 'shop_pdp' : 'studio_pdp'
    });
  }
}

export function handleVariantsColorClicked ({
  event,
  onUpdateSelectedAttribute
}) {
  const { name, status } = event.payload;
  if (status === 'unselected') {
    onUpdateSelectedAttribute({
      attributeType: 'color',
      attributeValue: name
    });
  }
}

export function handleVariantsDimsClicked ({
  event,
  onUpdateSelectedAttribute
}) {
  const { disabled, labelText, selected } = event.payload;
  if (!disabled && !selected) {
    onUpdateSelectedAttribute({
      attributeType: 'dimensions',
      attributeValue: labelText
    });
  }
}

export function handleAddlOptionsProductCardClicked ({
  event,
  initialProduct,
  onSelectAltProduct,
  variantProducts
}) {
  const { productId } = event.payload;
  const index = variantProducts.findIndex(p => p.id() === Number(productId));
  const selectedProduct = (index >= 0) ? variantProducts[index] : initialProduct;
  onSelectAltProduct({ index: index + 1, selectedProduct, source: 'addlProducts' });
}

export function handleSimilarProductsCardClicked ({
  alternativeProducts,
  event,
  onSelectAltProduct
}) {
  const { productId } = event.payload;
  const index = alternativeProducts.findIndex(p => p.id() === Number(productId));
  if (index >= 0) {
    const selectedProduct = alternativeProducts[index];
    onSelectAltProduct({ index, selectedProduct, source: 'similarProducts' });
  }
}

export async function handleAddonProductAddToCartClicked ({
  activeSpace,
  event,
  fromShop,
  onAddToCart,
  reduxDispatcher,
  setAddToCartProductId
}) {
  const { addon, disabled } = event.payload;
  if (disabled || addon.atcStatus !== 'not-in-cart') {
    return;
  }

  const { whProduct } = addon;
  try {
    // Start the addon ATC spinner
    setAddToCartProductId(whProduct.id());

    // Add the product to the cart.
    const design = fromShop
      ? undefined
      : reduxDispatcher(getCurrentActiveDesign());

    const atcDesignId = (design && design.id()) || null;
    await reduxDispatcher(addProductToCart({
      product: whProduct,
      quantity: 1,
      designId: atcDesignId
    }));

    // Common Analytics
    const atcEmail = reduxDispatcher(getCurrentUserEmail());
    const atcProduct = whProduct.warehouseProduct();
    if (atcDesignId) {
      atcProduct.designId = atcDesignId;
    }
    reduxDispatcher(userAddedProductFromPDP(atcEmail, atcProduct));

    if (fromShop) {
      // Shop specific post add-to-cart actions.
      onAddToCart({ isProductAddOn: true, product: whProduct });
    } else {
      // Studio Specific Analytics
      const userId = reduxDispatcher(getCurrentUserId());
      const selectedHotspotProduct = reduxDispatcher(getCurrentSelectedHotspotProduct());
      const searchKey = reduxDispatcher(getCurrentSearchKey());
      const searchProducts = reduxDispatcher(ProductSearch.getCurrentSearchResultFor(searchKey));
      const enabledFilters = reduxDispatcher(ProductSearch.getCurrentEnabledFiltersFor(searchKey));
      const packageData = reduxDispatcher(getCurrentPackageData());

      const studioPackage = packageData.find(studioPackage => studioPackage.sku === activeSpace.getPackageTypes()[0]);
      const packagePrice = studioPackage && studioPackage.price;

      reduxDispatcher(analytics.trackAddToCart({
        altProduct: null,
        design,
        enabledFilters,
        isProductAddOn: true,
        packagePrice,
        product: whProduct,
        searchKey,
        searchProducts,
        selectedHotspotProduct,
        studio: activeSpace,
        origin: 'PDP',
        userId
      }));
    }
  } catch (error) {
    bugsnagError(error);
  }

  setAddToCartProductId(null);
}

export function handleMainAddToCartClicked ({
  event,
  isBuyFromRetailer,
  onAddMainProductToCart,
  onBuyFromRetailer
}) {
  const { disabled } = event.payload;
  if (disabled) {
    return;
  }

  if (isBuyFromRetailer) {
    onBuyFromRetailer();
  } else {
    onAddMainProductToCart();
  }
}

export function handleSwapButtonClicked ({
  event,
  onSwapClick
}) {
  const { disabled } = event.payload;
  if (disabled) { return; }

  onSwapClick();
}
